import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { siteConfigService } from '../../services/siteConfig';
import type { SiteConfig } from '../../services/siteConfig';
import AuthModal from './AuthModal';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [authModal, setAuthModal] = useState<{ isOpen: boolean; mode: 'login' | 'register' }>({
    isOpen: false,
    mode: 'login'
  });
  const [siteConfig, setSiteConfig] = useState<SiteConfig>({
    show_title: true,
    site_title: 'VINTAGE LITERAL',
    logo_url: '',
    logo_width: 40,
    logo_height: 40,
    hero_image_url: '',
    hero_image_scale: 'cover',
    hero_image_position: 'left-top',
    hero_title: 'VINTAGE LITERAL',
    hero_subtitle: 'Explora la literatura\ncon mirada retro',
  });
  const [logoError, setLogoError] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadSiteConfig();
  }, []);

  const loadSiteConfig = async () => {
    try {
      const config = await siteConfigService.getSiteConfig();
      setSiteConfig(config);
      setLogoError(false); // Resetear error al cargar nueva configuración
    } catch (error) {
      console.error('Error loading site config:', error);
    }
  };

  const handleLogoError = () => {
    console.error('Logo failed to load:', siteConfig.logo_url);
    setLogoError(true);
  };

  const isValidImageUrl = (url: string) => {
    // Verificar si la URL termina con extensiones de imagen válidas
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    const lowerUrl = url.toLowerCase();
    return imageExtensions.some(ext => lowerUrl.includes(ext)) || 
           lowerUrl.includes('blob:') || 
           lowerUrl.includes('data:image/');
  };

  const handleAuthClick = (mode: 'login' | 'register') => {
    setAuthModal({ isOpen: true, mode });
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsMenuOpen(false);
      alert('Sesión cerrada exitosamente');
    } catch {
      alert('Error al cerrar sesión');
    }
  };


  return (
    <>
      <nav className="fixed top-0 left-0 right-0 vintage-paper aged-paper shadow-lg z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo/Title */}
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              {siteConfig.logo_url && !logoError && isValidImageUrl(siteConfig.logo_url) && (
                <img
                  src={siteConfig.logo_url}
                  alt="Logo"
                  width={siteConfig.logo_width}
                  height={siteConfig.logo_height}
                  className="object-contain"
                  onError={handleLogoError}
                  onLoad={() => setLogoError(false)}
                />
              )}
              {/* Mostrar mensaje de error del logo solo en desarrollo */}
              {siteConfig.logo_url && (logoError || !isValidImageUrl(siteConfig.logo_url)) && import.meta.env.DEV && (
                <div className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
                  Logo error
                </div>
              )}
              {siteConfig.show_title && (
                <span className="font-headings text-lg font-bold text-sepia-900 hover:text-sepia-700 transition-colors">
                  {siteConfig.site_title}
                </span>
              )}
            </button>

            {/* Right side buttons */}
            <div className="flex items-center gap-3">
              {/* Search Button */}
              <button
                onClick={() => navigate('/search')}
                className="w-8 h-8 flex items-center justify-center text-sepia-700 hover:text-sepia-900 transition-colors vintage-paper-subtle rounded-full hover:shadow-md"
              >
                <i className="ri-search-line text-sm"></i>
              </button>

              {/* Menu Button */}
              <div className="relative">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="w-8 h-8 flex items-center justify-center text-sepia-700 hover:text-sepia-900 transition-colors vintage-paper-subtle rounded-full hover:shadow-md"
                >
                  <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-sm`}></i>
                </button>

                {/* Dropdown Menu */}
                {isMenuOpen && (
                  <div className="fixed inset-0 z-50 flex justify-end">
                    {/* Overlay */}
                    <div 
                      className="absolute inset-0 bg-sepia-900/30 backdrop-blur-sm"
                      onClick={() => setIsMenuOpen(false)}
                    />
                    
                    {/* Menu Panel - 65% width, full height */}
                    <div className="relative w-[65%] h-full vintage-paper aged-paper shadow-2xl border-l border-sepia-300/50 overflow-y-auto">
                      {/* Header */}
                      <div className="p-4 border-b border-sepia-300/50 bg-sepia-100/30">
                        <div className="flex items-center justify-between mb-2">
                          <h2 className="font-headings text-lg text-sepia-900">MENÚ</h2>
                          <button
                            onClick={() => setIsMenuOpen(false)}
                            className="w-8 h-8 flex items-center justify-center text-sepia-700 hover:text-sepia-900 transition-colors vintage-paper-subtle rounded-full hover:shadow-md"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      </div>

                      {/* User Section */}
                      {isAuthenticated ? (
                        <div className="p-4 border-b border-sepia-300/50">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-sepia-200/60 rounded-full flex items-center justify-center">
                              <i className="ri-user-line text-sepia-600"></i>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-ui text-sepia-800 font-medium">{user?.fullName}</p>
                              <p className="text-xs text-sepia-600">{user?.email}</p>
                            </div>
                          </div>
                          {user?.isAffiliated && (
                            <span className="text-xs text-green-700 font-ui bg-green-100/50 px-3 py-1 rounded-full">
                              Usuario Afiliado
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="p-4 border-b border-sepia-300/50">
                          <h3 className="font-headings text-sm text-sepia-900 mb-3">ACCESO</h3>
                          <div className="space-y-2">
                            <button
                              onClick={() => handleAuthClick('login')}
                              className="w-full vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-4 py-2 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md flex items-center gap-2"
                            >
                              <i className="ri-login-box-line text-xs"></i>
                              Iniciar Sesión
                            </button>
                            <button
                              onClick={() => handleAuthClick('register')}
                              className="w-full vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-4 py-2 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md flex items-center gap-2"
                            >
                              <i className="ri-user-add-line text-xs"></i>
                              Crear Cuenta
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Navigation Section */}
                      <div className="p-4 border-b border-sepia-300/50">
                        <h3 className="font-headings text-sm text-sepia-900 mb-3">NAVEGACIÓN</h3>
                        <div className="space-y-1">
                          <button
                            onClick={() => {
                              navigate('/');
                              setIsMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm font-ui text-sepia-700 hover:text-sepia-900 hover:bg-sepia-100/50 rounded-[8%] transition-colors flex items-center gap-3"
                          >
                            <i className="ri-home-line"></i>
                            Inicio
                          </button>

                          <button
                            onClick={() => {
                              navigate('/search');
                              setIsMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm font-ui text-sepia-700 hover:text-sepia-900 hover:bg-sepia-100/50 rounded-[8%] transition-colors flex items-center gap-3"
                          >
                            <i className="ri-search-line"></i>
                            Buscar
                          </button>

                          {isAuthenticated && (
                            <>
                              <button
                                onClick={() => {
                                  navigate('/profile');
                                  setIsMenuOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 text-sm font-ui text-sepia-700 hover:text-sepia-900 hover:bg-sepia-100/50 rounded-[8%] transition-colors flex items-center gap-3"
                              >
                                <i className="ri-user-line"></i>
                                Mi Perfil
                              </button>

                              {user?.isAffiliated && (
                                <button
                                  onClick={() => {
                                    navigate('/create-blog');
                                    setIsMenuOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm font-ui text-sepia-700 hover:text-sepia-900 hover:bg-sepia-100/50 rounded-[8%] transition-colors flex items-center gap-3"
                                >
                                  <i className="ri-edit-line"></i>
                                  Crear Artículo
                                </button>
                              )}

                              {user?.role === 'admin' && (
                                <button
                                  onClick={() => {
                                    navigate('/admin');
                                    setIsMenuOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm font-ui text-sepia-700 hover:text-sepia-900 hover:bg-sepia-100/50 rounded-[8%] transition-colors flex items-center gap-3"
                                >
                                  <i className="ri-settings-line"></i>
                                  Panel de Administración
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* About Section */}
                      <div className="p-4 border-b border-sepia-300/50">
                        <h3 className="font-headings text-sm text-sepia-900 mb-3">SOBRE NOSOTROS</h3>
                        <div className="space-y-1">
                          <button
                            onClick={() => {
                              navigate('/about');
                              setIsMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm font-ui text-sepia-700 hover:text-sepia-900 hover:bg-sepia-100/50 rounded-[8%] transition-colors flex items-center gap-3"
                          >
                            <i className="ri-information-line"></i>
                            Acerca de
                          </button>

                          <button
                            onClick={() => {
                              navigate('/privacy');
                              setIsMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm font-ui text-sepia-700 hover:text-sepia-900 hover:bg-sepia-100/50 rounded-[8%] transition-colors flex items-center gap-3"
                          >
                            <i className="ri-shield-check-line"></i>
                            Política de Privacidad
                          </button>

                          <button
                            onClick={() => {
                              navigate('/contact');
                              setIsMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm font-ui text-sepia-700 hover:text-sepia-900 hover:bg-sepia-100/50 rounded-[8%] transition-colors flex items-center gap-3"
                          >
                            <i className="ri-mail-line"></i>
                            Contacto
                          </button>
                        </div>
                      </div>

                      {/* Logout Section */}
                      {isAuthenticated && (
                        <div className="p-4">
                          <button
                            onClick={handleLogout}
                            className="w-full text-left px-3 py-2 text-sm font-ui text-red-600 hover:text-red-800 hover:bg-red-50/50 rounded-[8%] transition-colors flex items-center gap-3"
                          >
                            <i className="ri-logout-box-line"></i>
                            Cerrar Sesión
                          </button>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="mt-auto p-4 border-t border-sepia-300/50 bg-sepia-50/30">
                        <p className="text-xs text-sepia-500 font-ui text-center">
                          © 2024 Vintage Literal
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModal.isOpen}
        onClose={() => setAuthModal({ ...authModal, isOpen: false })}
        mode={authModal.mode}
        onSwitchMode={(mode) => setAuthModal({ isOpen: true, mode })}
      />
    </>
  );
}
